/**
 * useFormShapeGuard — Filet de sécurité runtime pour détecter les bugs
 * du type « champ qui disparaît silencieusement après reset du formulaire ».
 *
 * Cas typique : un useEffect de reset oublie de propager certaines clés
 * présentes dans le state initial. Le champ devient `undefined` puis est filtré
 * par le backend (`if v is not None`), donc jamais persisté → toast « succès »
 * mais rien ne change en DB. Bug invisible.
 *
 * Le hook capture les clés du state au premier rendu et compare à chaque
 * mise à jour. En cas de divergence, un warning détaillé est loggé en console
 * (dev/preview only). En production : NO-OP.
 *
 * Usage :
 *   const [formData, setFormData] = useState(initialFormData);
 *   useFormShapeGuard('MaModale', formData);
 *
 * Optionnel : ignorer certaines clés conditionnelles
 *   useFormShapeGuard('MaModale', formData, { ignore: ['photo_url'] });
 *
 * @param {string} formName  — Nom du formulaire (ex: "EmployeeFormComplete")
 * @param {object} state     — Le state formData actuel
 * @param {object} options   — { ignore: string[] }
 */
import { useEffect, useRef } from 'react';

const isDev =
  typeof process !== 'undefined' &&
  process.env &&
  process.env.NODE_ENV !== 'production';

export function useFormShapeGuard(formName, state, options = {}) {
  const expectedKeysRef = useRef(null);
  const alreadyWarnedRef = useRef(new Set());

  // Capture le shape au premier rendu (où l'objet est encore intact)
  if (expectedKeysRef.current === null && state && typeof state === 'object') {
    expectedKeysRef.current = Object.keys(state);
  }

  useEffect(() => {
    if (!isDev) return;
    if (!state || typeof state !== 'object') return;
    const expected = expectedKeysRef.current;
    if (!expected || expected.length === 0) return;

    const ignore = new Set(options.ignore || []);
    const currentKeys = new Set(Object.keys(state));
    const missing = expected.filter(k => !currentKeys.has(k) && !ignore.has(k));
    if (missing.length === 0) return;

    // Évite le spam : un warning unique par signature de manquants
    const signature = missing.join(',');
    if (alreadyWarnedRef.current.has(signature)) return;
    alreadyWarnedRef.current.add(signature);

    // eslint-disable-next-line no-console
    console.warn(
      `[useFormShapeGuard] ⚠️ Formulaire « ${formName} » : ${missing.length} champ(s) disparu(s) du state après reset/update.\n` +
      `   Manquants : ${missing.join(', ')}\n` +
      `   ➤ Ces champs étaient présents au montage mais ont été perdus.\n` +
      `   ➤ Utilisez { ...initialFormData, ...overrides } dans votre setFormData pour éviter ce bug.\n` +
      `   ➤ Voir /app/memory/PRD.md section « Conventions de code ».`
    );
  }, [formName, state, options.ignore]);
}

export default useFormShapeGuard;
